import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useRequestContext } from "../hooks/useRequest";
import { requestTypes } from "../constants";
import { UserContext, useEntitlements } from "../../contexts";
import {
  OrderManagementProvider,
  useOrderManagement,
} from "../../orderManagement/useOrderManagement";
import { STEPS } from "../../orderManagement/constants";
import {
  SECTIONS,
  SECTION_IDS,
  SECTION_ORDER,
  getVisibleSections,
} from "./sectionConfig";

// Map OrderManagement STEPS → V2 section ID so PROCEED / SKIP / etc.
// inside cart-flow molecules drive the middle pane.
const STEP_TO_SECTION = {
  [STEPS.ASSIGN_SELL_REQUESTS]: SECTION_IDS.ASSIGN_SELL_REQUESTS,
  [STEPS.PX_SUMMARY]: SECTION_IDS.PX_SUMMARY,
  [STEPS.PAYMENTS]: SECTION_IDS.PAYMENTS,
  [STEPS.INVOICE]: SECTION_IDS.INVOICE,
  [STEPS.COMMISSIONS]: SECTION_IDS.COMMISSIONS,
  [STEPS.INTERNAL_DETAILS_AND_COSTS]: SECTION_IDS.INTERNAL_DETAILS,
  [STEPS.TRACKING_NUMBER]: SECTION_IDS.TRACKING_NUMBER,
};

// Reverse of STEP_TO_SECTION — used by ProviderAwareShell to advance
// om.step when the user clicks a section header. Without this, the
// outer accordion opens but each molecule's inner <Collapsible
// open={step === STEPS.X}> stays closed, leaving an empty body.
const SECTION_TO_STEP = Object.fromEntries(
  Object.entries(STEP_TO_SECTION).map(([step, section]) => [section, Number(step)])
);
import { RequestSectionNav } from "./RequestSectionNav";
import { RequestSectionView } from "./RequestSectionView";
import { RequestSmartCard } from "./RequestSmartCard";
import { useSectionDeepLink } from "./useSectionDeepLink";
import { Loading } from "../../components";
import { getBaseCurrency } from "../../core/currency";
import "./request-v2.scss";

// Swipe navigation (Alex, 2026-09-01): on a touch screen, a horizontal swipe
// on the section body moves to the previous/next section — every V2 flow
// (order / sell / px-order / sourcing / sell-enquiry) shares this shell, so
// they all get it. Guards keep the gesture honest:
//   - never hijack a swipe that starts inside a horizontally-scrollable
//     element (tables, the quote list) or a form control;
//   - demand a deliberate gesture (≥70px, fast, clearly more X than Y) so
//     vertical scrolling with a wobble never flips sections.
// Landing on a locked section matches tapping its dot: the locked
// placeholder shows with the unlock reason — a signpost, not a wall.
function useSectionSwipe(visibleSections, selectedSectionId, onSelect, onDirection) {
  const start = useRef(null);

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    let el = e.target;
    while (el && el !== e.currentTarget) {
      if (
        el.matches?.("input, textarea, select, [contenteditable], [data-noswipe]") ||
        el.scrollWidth > el.clientWidth + 4
      ) {
        start.current = null;
        return;
      }
      el = el.parentElement;
    }
    start.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };

  const onTouchEnd = (e) => {
    const s = start.current;
    start.current = null;
    if (!s) return;
    const t = e.changedTouches?.[0];
    if (!t || Date.now() - s.at > 600) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < 70 || Math.abs(dy) > 50 || Math.abs(dx) < Math.abs(dy) * 2) {
      return;
    }
    const idx = visibleSections.findIndex((sec) => sec.id === selectedSectionId);
    if (idx < 0) return;
    const next = dx < 0 ? idx + 1 : idx - 1; // swipe left = forward
    if (next >= 0 && next < visibleSections.length) {
      onDirection?.(dx < 0 ? 1 : -1);
      onSelect(visibleSections[next].id);
    }
  };

  return { onTouchStart, onTouchEnd };
}

function TemplateShell({
  baseState,
  enrichedState,
  visibleSections,
  selectedSectionId,
  onSelect,
  selectedQuote,
  onSelectQuote,
  deepLinkRef,
}) {
  const selectedSection = visibleSections.find(
    (s) => s.id === selectedSectionId
  );

  // `?section=<id>` (Dashboard v2 action links): land on that section once,
  // when it is visible. Runs here because this is where the FINAL visible set
  // (provider-enriched on the cart path) and the step-syncing onSelect meet.
  useSectionDeepLink(visibleSections, onSelect, deepLinkRef);

  // Swipe "landing" (Alex, 2026-09-01): the incoming section slides in from
  // the swipe direction (0 = tap/no slide), and the view scrolls back to the
  // section's top so every landing starts at the same place. Only the section
  // view is keyed/animated — the Order Summary card sits OUTSIDE __main and
  // never moves.
  const [slideDir, setSlideDir] = useState(0);
  const selectByTap = (id) => {
    setSlideDir(0);
    // The rep chose — a deep link that has not landed yet must not override it.
    if (deepLinkRef) deepLinkRef.current = true;
    onSelect(id);
  };
  const swipe = useSectionSwipe(
    visibleSections,
    selectedSectionId,
    onSelect,
    setSlideDir
  );
  useEffect(() => {
    // Desktop: the pane scrolls internally — reset it. Mobile: the page
    // scrolls — bring the top bar + section top back into view.
    const pane = document.getElementById("rc-request-v2-scroll");
    if (pane) pane.scrollTop = 0;
    if (window.matchMedia?.("(max-width: 1199px)")?.matches) {
      document
        .querySelector(".rc-request-v2")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedSectionId]);

  return (
    <main className="rc-request-v2" id="find-product">
      <div className="rc-request-v2__shell">
        <div className="rc-request-v2__panel">
          <RequestSectionNav
            sections={visibleSections}
            selectedSectionId={selectedSectionId}
            onSelect={selectByTap}
            state={enrichedState}
          />
          <section
            className="rc-request-v2__main"
            id="rc-request-v2-scroll"
            onTouchStart={swipe.onTouchStart}
            onTouchEnd={swipe.onTouchEnd}
          >
            <div
              key={selectedSectionId}
              className="rc-request-v2__section-slide"
              data-dir={slideDir}
            >
              <RequestSectionView
                section={selectedSection}
                sections={visibleSections}
                onSelect={onSelect}
                selectedQuote={selectedQuote}
                onSelectQuote={onSelectQuote}
                state={enrichedState}
              />
            </div>
          </section>
        </div>
        <RequestSmartCard
          sections={visibleSections}
          state={enrichedState}
          onSelect={onSelect}
        />
      </div>
    </main>
  );
}

// Inner component mounted ONLY when the OrderManagementProvider is
// active. Calls useOrderManagement to enrich the state object before
// passing it to the shell.
function ProviderAwareShell({
  baseState,
  visibleSections,
  selectedSectionId,
  onSelect,
  selectedQuote,
  onSelectQuote,
  deepLinkRef,
}) {
  const om = useOrderManagement();
  const { refreshRequest } = useRequestContext();

  // FX RULE (hooks/fx.js): when om has no rate for an amount it needs, its
  // converted totals were computed with a stand-in of 1. Hand the card null
  // instead (its own null paths then take over) plus the flag, so it can say
  // "Rate unavailable" rather than show a guessed figure.
  const omFxMissing = !!om?.fxMissing;
  const omFx = (value) => (omFxMissing ? null : value);
  const enrichedState = {
    ...baseState,
    fxMissing: omFxMissing,
    fxUnavailable: !!om?.fxUnavailable,
    paidAmount: omFx(om?.paidAmount),
    toPay: omFx(om?.toPay),
    commissionsAmount: omFx(om?.commissionsAmount),
    // The order's EXPERT FEE and who it relates to. Revenue the client paid on
    // top, already inside the profit that `commissionsAmount` splits — the card
    // shows it on its own row and says so, so it doesn't read as a deduction.
    expertFeeGbp: omFx(om?.expertFeeGbp),
    expertFeeName: om?.expertFee?.expert_account_name,
    netProfitBeforeCommissions: omFx(om?.netProfitBeforeCommissions),
    marketplaceFee: om?.marketplaceFee,
    // Order-currency → GBP rate for the smart card's GBP-pivot net-profit calc.
    sellingToGbpRate: om?.sellingToGbpRate,
    // GBP → base rate to display GBP-pivot figures in the merchant's currency.
    gbpToBaseRate: om?.gbpToBaseRate,
    // The DEAL currency as resolved by the provider (quote → order → listing
    // → base) — the smart card uses it instead of recomputing its own.
    // NOTE: `paidAmount` / `toPay` are denominated in THIS currency; anything
    // that labels them must use it (order 87362 labelled paidAmount with the
    // quote's currency instead, so a 26,850 EUR payment rendered "€22,976").
    listingRate: om?.listingRate,
    // Live payment rows in their ORIGINAL currency (amount + currency from the
    // same record, no FX applied) so the smart card can show what the client
    // actually paid rather than a pre-converted aggregate. See order 87362.
    paymentRows: om?.paymentRows,
    // Sell-quotes total ALREADY CONVERTED into the deal currency — the smart
    // card's PX Summary consumes this rather than summing raw quote prices.
    sellRequestsAmount: omFx(om?.sellRequestsAmount),
    getPdf: om?.getPdf,
    getPdfUrl: om?.getPdfUrl,
    sellRequests:
      (om?.sellRequests?.length ?? 0) > 0
        ? om.sellRequests
        : baseState.sellRequests,
    order: baseState.order ?? om?.order,
    // Selected quotes per request id — used by the smart card's
    // PX_SUMMARY block to compute Sell Quotes Total from the actual
    // selected quote (not the persisted last_quote, which can be stale
    // after admin changes the price via CHANGE QUOTE).
    quotes:
      om?.quotes && Object.keys(om.quotes).length > 0
        ? om.quotes
        : baseState.quotes,
  };
  // Recompute visibility from the enriched state so PX_SUMMARY (and
  // any other section that depends on om.sellRequests) becomes
  // visible the moment admin selects sell requests on an order
  // request that started as non-PX.
  const liveVisibleSections = getVisibleSections(enrichedState);

  // When a cart-flow molecule advances OrderManagement's step
  // (e.g. AssignSellRequests' PROCEED -> goToSummary, SKIP ->
  // skipSellRequests, Payments save -> goToInvoice), reflect that in
  // V2's selected section so the middle pane follows.
  //
  // Many setStep callsites in useOrderManagement.js advance to a
  // section that isn't yet visible under the new visibility rules
  // (e.g. quote approval → setStep(PAYMENTS), but PAYMENTS hides
  // until a listing is linked). When that happens, walk the section
  // order backward from the desired target to find the first section
  // that IS visible — that's where the rep can actually act.
  // Falls back to staying put if nothing reasonable is visible.
  const liveVisibleIds = liveVisibleSections.map((s) => s.id);
  useEffect(() => {
    const desired = STEP_TO_SECTION[om?.step];
    if (!desired) return;
    let target = desired;
    if (!liveVisibleIds.includes(target)) {
      // Walk the section order backward from desired to find the
      // closest visible upstream section. Doesn't jump *forward* —
      // a step advance should never skip past an actionable section
      // the rep hasn't visited yet.
      const desiredIdx = SECTION_ORDER.indexOf(desired);
      let found = null;
      for (let i = desiredIdx - 1; i >= 0; i--) {
        if (liveVisibleIds.includes(SECTION_ORDER[i])) {
          found = SECTION_ORDER[i];
          break;
        }
      }
      if (!found) return; // nothing visible upstream — stay put
      target = found;
    }
    if (target !== selectedSectionId) {
      onSelect(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [om?.step, liveVisibleIds.join(",")]);

  // Reverse sync: when admin clicks a section header in the v2 nav,
  // advance om.step so the molecule's inner <Collapsible
  // open={step === STEPS.X}> actually opens. AssignSellRequests uses
  // its own context flag (assignSellRequestsOpen) instead of step.
  const selectWithStepSync = (sectionId) => {
    onSelect(sectionId);
    if (sectionId === SECTION_IDS.ASSIGN_SELL_REQUESTS) {
      om?.setAssignSellRequestsOpen?.(true);
      return;
    }
    const targetStep = SECTION_TO_STEP[sectionId];
    if (targetStep != null && om?.step !== targetStep) {
      om?.setStep?.(targetStep);
    }
  };

  // Design 2 — sync the underlying request when om.order's px_request_id
  // changes. goToSummary (PROCEED) writes order.px_request_id to a new
  // id; dissolve-px (SKIP) clears it. Either flip needs to invalidate
  // useRequestContext.request so isPxFlow recomputes from fresh data,
  // and reopening the page sees the persisted state.
  const omOrderPxId = om?.order?.px_request_id ?? null;
  const didSyncPxRef = useRef(false);
  useEffect(() => {
    // Skip the initial mount: loadProducts has already fetched the request,
    // so re-fetching it here is redundant and triggers an extra render right
    // after the reveal gate opens — contributing to the SEARCH PRODUCT
    // catalog list resetting/flickering. Only resync when px_request_id
    // genuinely flips later (PROCEED writes a new id, SKIP clears it), which
    // is this effect's documented intent.
    if (!didSyncPxRef.current) {
      didSyncPxRef.current = true;
      return;
    }
    refreshRequest?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omOrderPxId]);

  // stillVisible fallback must use liveVisibleSections — the parent's
  // copy in RequestTemplateV2 is stale (baseState only). Without this
  // override, PROCEED → setStep(PX_SUMMARY) → onSelect("px_summary")
  // → parent stillVisible doesn't find it → resets to first section
  // (SEARCH PRODUCT).
  const liveSectionsKey = liveVisibleSections.map((s) => s.id).join(",");
  useEffect(() => {
    const stillVisible = liveVisibleSections.some(
      (s) => s.id === selectedSectionId
    );
    if (!stillVisible && liveVisibleSections.length > 0) {
      onSelect(liveVisibleSections[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSectionsKey, selectedSectionId]);

  return (
    <TemplateShell
      baseState={baseState}
      enrichedState={enrichedState}
      visibleSections={liveVisibleSections}
      selectedSectionId={selectedSectionId}
      onSelect={selectWithStepSync}
      selectedQuote={selectedQuote}
      onSelectQuote={onSelectQuote}
      deepLinkRef={deepLinkRef}
    />
  );
}

export function RequestTemplateV2() {
  const {
    request,
    requestType,
    order,
    product,
    client,
    pxRequests,
    pxProducts,
    additionalMainProducts,
    additionalMainRequests,
    journey,
    listing,
    quotes,
    refreshRequest,
    isInitialLoading,
    flowVariant,
  } = useRequestContext();
  const { user } = useContext(UserContext);
  const { hasFeature } = useEntitlements();

  // Design 2 — PX-ness is data-driven, not URL-driven. A regular order
  // request URL (/request/v2/order/:id) is still PX if the underlying
  // order/request carries a px_request_id. Conversely a px-order URL
  // whose link has been dissolved reverts to non-PX automatically.
  const isPxFlow =
    [requestTypes.PX_ORDER, requestTypes.PX_CART].includes(requestType) ||
    !!request?.px_request_id ||
    !!order?.px_request_id;

  const quoteId = request?.last_quote?.id ?? request?.last_quote_id;
  const quoteListingId = request?.last_quote?.listing_id;
  // Active Listings source writes request.linked_listing_id directly
  // without creating a quote first — accept either path so primary's
  // listing feeds into seededListings regardless of source.
  const primaryListingId = quoteListingId ?? request?.linked_listing_id ?? null;

  const [fetchedListing, setFetchedListing] = useState(null);
  // Tracks whether the primary slot's linked-listing fetch below has
  // settled. Used by the reveal gate so the page (and the OM provider it
  // mounts) waits for the listing to land — otherwise Payments / Balance
  // Due paint off an empty listings array and visibly correct themselves.
  const [primaryListingLoaded, setPrimaryListingLoaded] = useState(false);
  // Per-slot listings for additional main Requests (slots 1 & 2). Keyed
  // by additionalMainRequests[i].id; each value is the resolved
  // Listing fetched via the slot's linked_listing_id or
  // last_quote.listing_id. Multi-main consolidates these into
  // seededListings below so the unified Order ends up with one
  // OrderItem per main Request.
  const [additionalSlotListings, setAdditionalSlotListings] = useState({});
  const orderListings = useMemo(
    () => (order?.items ?? []).map((i) => i.listing).filter(Boolean),
    [order]
  );

  useEffect(() => {
    if (!user?.is_admin) return;
    if (orderListings.length > 0) return;
    if (!primaryListingId) return;
    let cancelled = false;
    // Prefer POST /api/quotes/{quoteId}/listing only when the quote
    // actually has a listing (returns it via the quote.listing relation).
    // Fall back to GET /api/listings/{id} for the Active-Listings path
    // where the rep linked a listing (request.linked_listing_id) without
    // ever creating one from the quote — keying on quoteId alone would
    // POST and get null here instead of loading the linked listing.
    const fetch = quoteListingId
      ? axios.post(`/api/quotes/${quoteId}/listing`)
      : axios.get(`/api/listings/${primaryListingId}`);
    fetch
      .then(({ data }) => {
        if (!cancelled) setFetchedListing(data);
      })
      .catch(() => {
        if (!cancelled) setFetchedListing(null);
      })
      .finally(() => {
        if (!cancelled) setPrimaryListingLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.is_admin, orderListings.length, quoteId, primaryListingId]);

  // Fetch each additional main slot's linked listing — fans out one
  // request per slot, dedupes by listing id. Only runs when there's
  // no pre-existing Order (which would already carry every line item
  // through orderListings).
  useEffect(() => {
    if (!user?.is_admin) return;
    if (orderListings.length > 0) return;
    const slots = additionalMainRequests ?? [];
    let cancelled = false;
    slots.forEach((slotReq) => {
      const slotListingId =
        slotReq?.linked_listing_id ?? slotReq?.last_quote?.listing_id;
      if (!slotListingId) return;
      if (additionalSlotListings[slotReq.id]) return;
      axios
        .get(`/api/listings/${slotListingId}`)
        .then(({ data }) => {
          if (cancelled || !data) return;
          setAdditionalSlotListings((prev) => ({ ...prev, [slotReq.id]: data }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, [
    user?.is_admin,
    orderListings.length,
    additionalMainRequests,
    additionalSlotListings,
  ]);

  // Final seeded listings: order.items if Order already exists,
  // otherwise primary slot's listing + every additional slot's
  // listing. The OrderManagement flow downstream creates one
  // OrderItem per listing, so this is what makes multi-main produce
  // multi-line cart Orders.
  const seededListings = useMemo(() => {
    // `listings.currency` exists (migration 2026_07_21_150001) and is the
    // listing's own SELL-price currency — it must never be overwritten here.
    // The DEAL currency (what the client is billed in) is a separate concept and
    // travels as `deal_currency`. The order flow's `quotes` map (selected quotes)
    // doesn't reliably carry the buy quote —
    // so the DEAL currency (what the client is billed in) must come from the
    // request's quote. Resolve it per listing from the quote linked to it (by
    // listing_id), looking across the main request, the multi-main slots, and
    // the selected-quotes map. Without it the whole order flow defaults to GBP
    // and a non-GBP deal (e.g. a $9,300 USD quote) is shown/charged in £.
    //
    // INVARIANT (real case: order 87362) — the deal currency travels in its
    // OWN field (`deal_currency`) and must never overwrite `currency`. It used
    // to: a €26,850 quote stamped `currency: 'eur'` onto a listing whose
    // price is £23,500, so every consumer that dutifully read "the amount and
    // the currency off the same record" still got a GBP amount wearing a €
    // sign ("€23,500" in the Order Summary SHOPPING CART). A listing price
    // with no currency of its own is LEGACY GBP — it never inherits the deal's.
    const quoteCandidates = [
      request?.last_quote,
      ...(additionalMainRequests ?? []).map((r) => r?.last_quote),
      ...Object.values(quotes || {}),
    ].filter(Boolean);
    const stampCurrency = (l) => {
      if (!l) return l;
      const q = quoteCandidates.find((qt) => qt?.listing_id === l.id);
      return {
        ...l,
        // The listing's own price currency: null/absent ⇒ legacy GBP.
        currency: (l.currency || "gbp").toLowerCase(),
        // Quote-derived deal currency, for the order/payment flow.
        deal_currency: (
          q?.currency ||
          l.currency ||
          getBaseCurrency()
        ).toLowerCase(),
      };
    };

    const base =
      orderListings.length > 0
        ? orderListings
        : (() => {
            const out = [];
            if (fetchedListing) out.push(fetchedListing);
            (additionalMainRequests ?? []).forEach((slotReq) => {
              const slotListing = additionalSlotListings[slotReq.id];
              if (slotListing) out.push(slotListing);
            });
            return out;
          })();

    // Dedupe by listing id (rare but possible if two slots share)
    const seen = new Set();
    return base
      .filter((l) => {
        if (!l?.id) return true;
        if (seen.has(l.id)) return false;
        seen.add(l.id);
        return true;
      })
      .map(stampCurrency);
  }, [
    orderListings,
    fetchedListing,
    additionalMainRequests,
    additionalSlotListings,
    request,
    quotes,
  ]);

  const baseState = {
    user,
    request,
    requestType,
    // Purpose-built variant descriptor (null on the stock flows). Consumed by
    // getVisibleSections for the section set + labels, and threaded down to
    // SEARCH PRODUCT / CONFIRM REQUEST for their tab + button overrides.
    flowVariant,
    isPxFlow,
    order,
    product,
    client,
    listings: seededListings,
    sellRequests: pxRequests ?? [],
    listing,
    pxProducts: pxProducts ?? [],
    // additionalMain* — drives the dynamic ADD PRODUCT (n/m) +
    // CONFIRM PRODUCT (n/m) sections for the 2nd/3rd main slots of
    // a multi-main deal. Slot 0 stays on the legacy singular state.
    additionalMainProducts: additionalMainProducts ?? [],
    additionalMainRequests: additionalMainRequests ?? [],
    journey: journey ?? [],
    quotes,
    // Plan gating: sections declaring a `feature` are dropped when the store's
    // plan doesn't include it (agreements, Xero PO — see sectionConfig).
    hasFeature,
  };

  const visibleSections = getVisibleSections(baseState);

  const [selectedSectionId, setSelectedSectionId] = useState(
    visibleSections[0]?.id ?? SECTION_IDS.SEARCH_PRODUCT
  );

  // Has the REP chosen a section, as opposed to the flow landing on one? Until
  // they have, the selection follows whatever section the flow actually starts
  // with. It cannot be decided at mount: `visibleSections` is derived from the
  // request, the client and the flow variant, none of which are loaded on the
  // first render — so the initialiser above always fell through to its
  // SEARCH_PRODUCT fallback, and the effect below only ever corrected a
  // selection that had become INVISIBLE. SEARCH_PRODUCT is visible in every
  // flow, so on a client-first flow (Sourcing / Sell Enquiry / Record a Sale,
  // which hoist ASSIGN_CLIENT via `sectionFirst`) the nav highlighted SEARCH
  // PRODUCT while ASSIGN CLIENT sat first and open.
  const repChoseSection = useRef(false);
  // `?section=` deep link applied (or overruled by the rep). Lives here, not in
  // the shell, so an OrderManagementProvider remount cannot re-apply it.
  const deepLinkRef = useRef(false);
  const selectSection = (id) => {
    repChoseSection.current = true;
    setSelectedSectionId(id);
  };

  // Quote selected via SEND QUOTE's checkmark. When set, CONNECT
  // LISTING and the smart card target this quote instead of
  // request.last_quote. null = "use last_quote" default.
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Mount the OrderManagementProvider as soon as we have an admin +
  // client. Previously gated on seededListings.length > 0, which
  // meant that PX SUMMARY (and other cart-flow molecules) crashed
  // when the rep finalised PX before any quote was created — the
  // section selected but no provider was mounted, so PxSummary's
  // useOrderManagement() threw "Component must be wrapped with
  // Provider" and the ErrorBoundary recovery looked like a page
  // reload back to SEARCH PRODUCT.
  // The provider handles initialListings=[] gracefully; downstream
  // cart-flow molecules that actually need listings (e.g.
  // registerDepositsOrder) gate their own actions.
  const providerEnabled = !!user?.is_admin && !!client;

  useEffect(() => {
    // When the OrderManagementProvider is mounted, ProviderAwareShell
    // owns the stillVisible check (it has access to the enriched
    // visibleSections that include om.sellRequests). Running this
    // fallback against baseState would clobber PROCEED → PX_SUMMARY
    // navigations and bounce the admin back to SEARCH PRODUCT.
    if (providerEnabled) return;
    if (visibleSections.length === 0) return;
    const first = visibleSections[0].id;
    const stillVisible = visibleSections.some(
      (s) => s.id === selectedSectionId
    );
    // The selection fell out of the flow — land on whatever is first now.
    if (!stillVisible) {
      setSelectedSectionId(first);
      return;
    }
    // Still on the mount-time guess. Follow the flow's real first section now
    // that it is known. Guarded by `repChoseSection` so this never drags the
    // rep back to the top once they have navigated themselves.
    if (!repChoseSection.current && selectedSectionId !== first) {
      setSelectedSectionId(first);
    }
  }, [visibleSections, selectedSectionId, providerEnabled]);

  // ── Reveal gate ────────────────────────────────────────────────────
  // Hold a full-page loader until the request is fully hydrated, so the
  // rep never sees (or clicks into) a half-populated shell. Three things
  // must be in before we reveal:
  //   1. The hook's initial fetch chain — request / product / client /
  //      order / group siblings / px overlay (isInitialLoading).
  //   2. The primary slot's linked listing, when there is one to fetch —
  //      drives Payments / Balance Due (primaryListingLoaded).
  //   3. Each additional main slot's listing on a multi-main deal.
  // A pre-existing Order already carries its line items, so when
  // orderListings is populated no listing fetch runs and those gates are
  // skipped. New requests have isInitialLoading=false from the start.
  // This gate only applies to the FIRST reveal. Once latched open via
  // `revealed`, it never closes again — otherwise a listing id appearing
  // later in the flow (e.g. the rep creates a quote, populating
  // primaryListingId) would flip `ready` false and slam a loader over a
  // page the rep is actively working in.
  const needsPrimaryListing =
    !!user?.is_admin && orderListings.length === 0 && !!primaryListingId;
  const expectedSlotListingIds = (additionalMainRequests ?? [])
    .filter((r) => r?.linked_listing_id || r?.last_quote?.listing_id)
    .map((r) => r.id);
  const needsAdditionalSlots =
    !!user?.is_admin &&
    orderListings.length === 0 &&
    expectedSlotListingIds.length > 0;
  const ready =
    !isInitialLoading &&
    (!needsPrimaryListing || primaryListingLoaded) &&
    (!needsAdditionalSlots ||
      expectedSlotListingIds.every((id) => additionalSlotListings[id]));

  // Seed from the first-render readiness so a new request (ready from the
  // start) reveals immediately with no spinner flash, while an existing
  // request that's still loading starts gated.
  const [revealed, setRevealed] = useState(ready);
  useEffect(() => {
    if (ready && !revealed) setRevealed(true);
  }, [ready, revealed]);

  if (!revealed) {
    // Themed thin purple spinner (rc-loading), centred in the page with a
    // status label while the request hydrates.
    return (
      <main className="rc-request-v2 rc-request-v2--loading" id="find-product">
        <Loading type="loading" text="Initialising request" />
      </main>
    );
  }

  if (!providerEnabled) {
    return (
      <TemplateShell
        baseState={baseState}
        enrichedState={baseState}
        visibleSections={visibleSections}
        selectedSectionId={selectedSectionId}
        onSelect={selectSection}
        selectedQuote={selectedQuote}
        onSelectQuote={setSelectedQuote}
        deepLinkRef={deepLinkRef}
      />
    );
  }

  // Force OrderManagementProvider to remount when the underlying data
  // shape changes mid-flow. OM only reads its `initial*` props once at
  // mount, so any subsequent change to listings / pxRequests / px linkage
  // would otherwise leave OM with stale state:
  //   - Finalise Part Exchange: pxRequests goes from [] → [the freshly
  //     picked sells]. Without remount, PX SUMMARY rendered empty.
  //   - Active-listing primary on a fresh load: seededListings hydrates
  //     ASYNCHRONOUSLY (axios.get /api/listings/{primaryListingId}) after
  //     the provider has already mounted with initialListings=[]. Without
  //     remount, `deposits` state stays {} (seedDepositsForListings only
  //     runs at init), so the PAYMENTS section's "Deposit Paid:" header
  //     renders without any payment rows, and toPay derives off an empty
  //     listings array (initial Balance Due wrong, settles on reload).
  // A composite key over (request id, px_request_id, sell ids, listing
  // ids) triggers a re-mount whenever any of those compositions change,
  // giving OM a freshly-initialised provider that picks up the latest
  // initial* props — mirroring the manual-reload behaviour without the
  // visible page flash.
  const omRemountKey = [
    request?.id ?? "no-req",
    request?.px_request_id ?? "no-px",
    (pxRequests ?? [])
      .filter((sr) => sr?.id)
      .map((sr) => sr.id)
      .sort()
      .join(","),
    (seededListings ?? [])
      .filter((l) => l?.id)
      .map((l) => l.id)
      .sort()
      .join(","),
  ].join("|");

  return (
    <OrderManagementProvider
      key={omRemountKey}
      client={client}
      mode="inline"
      initialListings={seededListings}
      initialStep={STEPS.ASSIGN_SELL_REQUESTS}
      initialSellRequests={pxRequests ?? []}
      initialIsPxOrder={isPxFlow}
      editingOrder={order || undefined}
      closeEditingOrder={() => {}}
      // Pass parent Order Request id so goToSummary's PROCEED can
      // create the PxRequest server-side via request_id when no cart
      // Order exists yet.
      parentRequestId={request?.id}
      // Persisted quote selections from the PxRequest — seeds
      // om.quotes so reload restores the CHANGE QUOTE checkmarks.
      initialQuotes={quotes}
      // PROCEED / SKIP backend writes need to refresh the parent
      // request locally so V2 picks up the new px_request_id and
      // hydrates pxRequests / pxProducts via the Design 2 overlay.
      onRequestRefresh={refreshRequest}
      // Preferred default user for the COMMISSIONS Slot 1. OM promotes
      // them only if is_sales_admin; otherwise falls back to the
      // logged-in admin.
      defaultCommissionUser={request?.created_by}
    >
      <ProviderAwareShell
        baseState={baseState}
        visibleSections={visibleSections}
        selectedSectionId={selectedSectionId}
        onSelect={selectSection}
        selectedQuote={selectedQuote}
        onSelectQuote={setSelectedQuote}
        deepLinkRef={deepLinkRef}
      />
    </OrderManagementProvider>
  );
}
